// PreviewTester/utils/templateGenerator.js

/**
 * ============================================================
 * ✅ NEW: Detect all top-level and nested arrays in a response object.
 * Returns an array of { path, key, length, fields, parentArrayPath, isNested }.
 *
 * NOTE: For nested array paths, we do NOT include "[0]" in the path.
 * The path is the human-readable access path (e.g., "response_data.bookings.guests").
 * ============================================================
 */
export const detectNestedArrays = (obj, parentPath = 'response_data') => {
  const arrays = [];

  const traverse = (data, path, isNested, parentArrayPath = null) => {
    if (!data || typeof data !== 'object') return;

    if (Array.isArray(data)) {
      if (data.length > 0 && typeof data[0] === 'object' && data[0] !== null) {
        arrays.push({
          path: path,
          key: path.split('.').pop(),
          length: data.length,
          fields: Object.keys(data[0] || {}),
          isNested: isNested,
          parentArrayPath: parentArrayPath,
        });

        // Look for nested arrays inside the first item of this array
        for (const [key, value] of Object.entries(data[0])) {
          if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
            traverse(value, `${path}.${key}`, true, path);
          }
        }
      }
      return;
    }

    // Object: traverse all keys
    for (const [key, value] of Object.entries(data)) {
      if (Array.isArray(value) && value.length > 0) {
        traverse(value, `${path}.${key}`, isNested, parentArrayPath);
      } else if (value && typeof value === 'object') {
        traverse(value, `${path}.${key}`, isNested, parentArrayPath);
      }
    }
  };

  traverse(obj, parentPath, false, null);
  return arrays;
};

/**
 * ============================================================
 * ✅ NEW: Generate a Case 3 template (multiple screens) from an object
 * where a specific array is chosen to create screens.
 *
 * @param {object} data - The full response object
 * @param {string} arrayPath - The path to the array that creates screens
 *                             e.g. "response_data.bookings"
 * @returns {string} - The generated Nunjucks template
 * ============================================================
 */
export const generateCase3Template = (data, arrayPath) => {
  console.log('[generateCase3Template] data:', data);
  console.log('[generateCase3Template] arrayPath:', arrayPath);

  if (!data || typeof data !== 'object' || !arrayPath) {
    return '';
  }

  // ============================================================
  // ✅ Resolve the array using the path
  // Handles paths like "response_data.bookings" or "response_data.bookings.guests"
  // When we encounter an array while traversing, we enter its first item
  // ============================================================
  const pathParts = arrayPath.replace(/^response_data\./, '').split('.');

  let array = data;
  for (const part of pathParts) {
    // If current is an array, enter its first item to continue traversal
    if (Array.isArray(array)) {
      if (array.length === 0) {
        console.warn('[generateCase3Template] Empty array in path:', arrayPath);
        return '';
      }
      array = array[0];
    }

    if (array && typeof array === 'object' && part in array) {
      array = array[part];
    } else {
      console.warn('[generateCase3Template] Could not resolve path:', arrayPath);
      return '';
    }
  }

  if (!Array.isArray(array) || array.length === 0) {
    console.warn('[generateCase3Template] Array is empty or invalid');
    return '';
  }

  const firstItem = array[0];
  if (!firstItem || typeof firstItem !== 'object') {
    return '';
  }

  // Item variable name (singular form of last path part)
  const rawKey = pathParts[pathParts.length - 1];
  const itemVar = getSingularForm(rawKey);

  const lines = [];
  lines.push(`{% for ${itemVar} in ${arrayPath} %}`);

  // Iterate fields of the first item
  for (const [key, value] of Object.entries(firstItem)) {
    // Skip null/undefined
    if (value === null || value === undefined) continue;

    // Nested array → inner loop
    if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object') {
      const innerKey = key;
      const innerVar = getSingularForm(innerKey);

      // Section header
      const sectionName = formatSectionName(innerKey);
      lines.push(`    ${sectionName}:`);
      lines.push(`    {% for ${innerVar} in ${itemVar}.${innerKey} %}`);

      // Inner fields
      for (const [innerField, innerValue] of Object.entries(value[0])) {
        if (innerValue === null || innerValue === undefined) continue;
        lines.push(`        ${innerField} - {{ ${innerVar}.${innerField} }}`);
      }

      lines.push(`    {% endfor %}`);
      continue;
    }

    // Nested object (not array) → section
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const sectionName = formatSectionName(key);
      lines.push(`    ${sectionName}:`);
      for (const [nestedKey, nestedValue] of Object.entries(value)) {
        if (nestedValue === null || nestedValue === undefined) continue;
        lines.push(`        ${nestedKey} - {{ ${itemVar}.${key}.${nestedKey} }}`);
      }
      continue;
    }

    // Primitive field
    lines.push(`    ${key} - {{ ${itemVar}.${key} }}`);
  }

  lines.push(`{% endfor %}`);

  return lines.join('\n');
};

/**
 * Generate a Nunjucks template from a JSON sample response
 * Completely agnostic - works with ANY JSON structure
 * @param {object} data - The parsed JSON data
 * @param {string} rootKey - The root key for the template (default: 'response_data')
 * @returns {string} - Generated Nunjucks template
 */
export const generateTemplateFromJSON = (data, rootKey = 'response_data') => {
  // Return empty string if data is invalid
  if (!data || typeof data !== 'object') {
    return '';
  }

  // Handle array at root level - THIS IS CASE 1
  if (Array.isArray(data)) {
    if (data.length === 0) return '';
    // Pass true for isRootArray to indicate this is the root array
    return generateRootArrayTemplate(data, rootKey);
  }

  // Handle object at root level - CASE 2
  const result = generateObjectTemplate(data, rootKey, 0, false);
  return Array.isArray(result) ? result.join('\n') : (result || '');
};

/**
 * Generate template for a root-level array (Case 1)
 * No section header for root arrays - the data IS the content
 */
const generateRootArrayTemplate = (arr, rootKey) => {
  const lines = [];

  // Get the first item to infer structure
  const firstItem = arr[0];
  if (!firstItem) return lines;

  // ============================================================
  // FIX: Use 'item' as the loop variable name for root arrays
  // This avoids confusion when the rootKey is also 'response_data'
  // ============================================================
  const itemKey = 'item';  // Use a generic name instead of getSingularForm(rootKey)
  console.log(`[generateRootArrayTemplate] Using '${itemKey}' as loop variable for root array`);

  // Add for loop start - using 'item' as the loop variable
  lines.push(`{% for ${itemKey} in ${rootKey} %}`);

  // Generate template for the first item - pass isArrayItem=true and isRootArray=true
  if (typeof firstItem === 'object' && !Array.isArray(firstItem) && firstItem !== null) {
    const nestedLines = generateObjectTemplate(firstItem, itemKey, 1, true);
    if (Array.isArray(nestedLines)) {
      // Add indentation to nested lines
      nestedLines.forEach(line => {
        if (line && line.trim()) {
          lines.push(`  ${line}`);
        } else {
          lines.push('');
        }
      });
    }
  } else {
    // Primitive array
    lines.push(`  {{ ${itemKey} }}`);
  }

  // Add for loop end
  lines.push(`{% endfor %}`);

  return lines.join('\n');
};

/**
 * Generate template for an object
 * Completely agnostic - no hardcoded field names, no assumptions
 * @param {object} obj - The object to generate template for
 * @param {string} rootKey - The root key path
 * @param {number} depth - The current depth
 * @param {boolean} isArrayItem - Whether this is an item inside an array
 * @returns {string[]} - Array of template lines
 */
const generateObjectTemplate = (obj, rootKey, depth, isArrayItem = false) => {
  const lines = [];
  const indent = '  '.repeat(depth);

  // Get the section name from the rootKey
  const sectionName = rootKey.split('.').pop() || '';

  // For array items, output fields directly without section headers
  if (isArrayItem) {
    for (const [key, value] of Object.entries(obj)) {
      if (value === null || value === undefined) {
        continue;
      }

      // Handle nested objects inside array items
      if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
        if (Object.keys(value).length === 0) {
          lines.push(`${indent}${key}: {}`);
          continue;
        }
        // For nested objects inside array items, add a section header
        const sectionDisplayName = formatSectionName(key);
        lines.push(`${indent}${sectionDisplayName}:`);
        const nestedLines = generateObjectTemplate(value, `${rootKey}.${key}`, depth + 1, true);
        if (Array.isArray(nestedLines)) {
          lines.push(...nestedLines);
        }
        continue;
      }

      // Handle arrays inside array items
      if (Array.isArray(value)) {
        if (value.length === 0) {
          lines.push(`${indent}${key}: []`);
          continue;
        }
        // For nested arrays, we want to keep indentation
        const arrayTemplate = generateNestedArrayTemplate(value, `${rootKey}.${key}`, depth);
        if (Array.isArray(arrayTemplate)) {
          lines.push(...arrayTemplate);
        }
        continue;
      }

      // Handle primitive values - use original key name exactly as is
      lines.push(`${indent}${key} - {{ ${rootKey}.${key} }}`);
    }
    return lines;
  }

  // For top-level objects (not array items) - CASE 2
  for (const [key, value] of Object.entries(obj)) {
    if (value === null || value === undefined) {
      continue;
    }

    // Handle nested objects
    if (typeof value === 'object' && !Array.isArray(value) && value !== null) {
      if (Object.keys(value).length === 0) {
        lines.push(`${indent}${key}: {}`);
        continue;
      }

      // Add section header for nested object - use key as-is, just format nicely
      const sectionDisplayName = formatSectionName(key);
      lines.push(`${indent}${sectionDisplayName}:`);

      // Recursively generate template for nested object
      const nestedLines = generateObjectTemplate(value, `${rootKey}.${key}`, depth + 1, false);
      if (Array.isArray(nestedLines)) {
        lines.push(...nestedLines);
      }
      continue;
    }

    // Handle arrays
    if (Array.isArray(value)) {
      if (value.length === 0) {
        lines.push(`${indent}${key}: []`);
        continue;
      }

      // Generate template for array items
      const arrayTemplate = generateArrayTemplate(value, `${rootKey}.${key}`, depth);
      if (Array.isArray(arrayTemplate)) {
        lines.push(...arrayTemplate);
      }
      continue;
    }

    // Handle primitive values - use original key name exactly as is
    lines.push(`${indent}${key} - {{ ${rootKey}.${key} }}`);
  }

  return lines;
};

/**
 * Generate template for a nested array (inside an object or array item)
 * Includes section header with proper indentation
 */
const generateArrayTemplate = (arr, rootKey, depth = 0) => {
  const lines = [];
  const indent = '  '.repeat(depth);

  // Get the first item to infer structure
  const firstItem = arr[0];
  if (!firstItem) return lines;

  // Determine the item key name (singular form)
  const sectionName = rootKey.split('.').pop() || 'items';
  const itemKey = getSingularForm(sectionName);
  const displaySectionName = formatSectionName(sectionName);

  // Add section header above the for loop
  lines.push(`${indent}${displaySectionName}:`);

  // Add for loop start
  lines.push(`${indent}{% for ${itemKey} in ${rootKey} %}`);

  // Generate template for the first item - pass isArrayItem=true
  if (typeof firstItem === 'object' && !Array.isArray(firstItem) && firstItem !== null) {
    const nestedLines = generateObjectTemplate(firstItem, itemKey, depth + 1, true);
    if (Array.isArray(nestedLines)) {
      // Add indentation to nested lines
      nestedLines.forEach(line => {
        if (line && line.trim()) {
          lines.push(`  ${line}`);
        } else {
          lines.push('');
        }
      });
    }
  } else {
    // Primitive array
    lines.push(`${indent}  {{ ${itemKey} }}`);
  }

  // Add for loop end
  lines.push(`${indent}{% endfor %}`);

  return lines;
};

/**
 * Generate template for a nested array without section header
 * Used for nested arrays inside array items (like guests inside booking)
 */
const generateNestedArrayTemplate = (arr, rootKey, depth = 0) => {
  const lines = [];
  const indent = '  '.repeat(depth);

  // Get the first item to infer structure
  const firstItem = arr[0];
  if (!firstItem) return lines;

  // Determine the item key name (singular form)
  const sectionName = rootKey.split('.').pop() || 'items';
  const itemKey = getSingularForm(sectionName);
  const displaySectionName = formatSectionName(sectionName);

  // Add section header with proper indentation
  lines.push(`${indent}${displaySectionName}:`);

  // Add for loop start
  lines.push(`${indent}{% for ${itemKey} in ${rootKey} %}`);

  // Generate template for the first item - pass isArrayItem=true
  if (typeof firstItem === 'object' && !Array.isArray(firstItem) && firstItem !== null) {
    const nestedLines = generateObjectTemplate(firstItem, itemKey, depth + 1, true);
    if (Array.isArray(nestedLines)) {
      // Add indentation to nested lines
      nestedLines.forEach(line => {
        if (line && line.trim()) {
          lines.push(`  ${line}`);
        } else {
          lines.push('');
        }
      });
    }
  } else {
    // Primitive array
    lines.push(`${indent}  {{ ${itemKey} }}`);
  }

  // Add for loop end
  lines.push(`${indent}{% endfor %}`);

  return lines;
};

/**
 * Format a section name for display
 * Completely agnostic - no hardcoded lists, purely string transformation
 */
const formatSectionName = (key) => {
  if (!key) return '';

  // Replace underscores with spaces
  let formatted = key.replace(/_/g, ' ');

  // Capitalize each word
  formatted = formatted.split(' ').map(word => {
    if (word.length === 0) return word;
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  }).join(' ');

  return formatted;
};

/**
 * Get singular form of a word
 * Completely agnostic - uses rules, no hardcoded mappings
 */
const getSingularForm = (word) => {
  if (!word) return 'item';

  // If it's a single character or doesn't end with 's', return as-is
  if (word.length <= 1 || !word.endsWith('s')) {
    return word;
  }

  // Handle special cases using rules (not hardcoded words)
  // Check for common plural patterns using regex
  const pluralPatterns = [
    { pattern: /ies$/, replacement: 'y' },
    { pattern: /ves$/, replacement: 'fe' },
    { pattern: /xes$/, replacement: 'x' },
    { pattern: /ses$/, replacement: 's' },
    { pattern: /ches$/, replacement: 'ch' },
    { pattern: /shes$/, replacement: 'sh' },
    { pattern: /zes$/, replacement: 'ze' },
    { pattern: /es$/, replacement: '' },
  ];

  for (const rule of pluralPatterns) {
    if (rule.pattern.test(word)) {
      const result = word.replace(rule.pattern, rule.replacement);
      // If the result is empty or same as input, fall through
      if (result && result !== word) {
        return result;
      }
    }
  }

  // Default: remove trailing 's'
  const result = word.slice(0, -1);
  // If removing 's' makes it too short or empty, return original
  if (result.length < 1) {
    return word;
  }
  return result;
};

export default generateTemplateFromJSON;